import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Shield, Zap, Phone, QrCode, MessageCircle, CheckCircle, Star, Mic, MicOff, Send, Play, ChevronRight, Menu, X, Globe, Siren, BellRing, Car, Heart, Smartphone } from "lucide-react";
import { AnimatePresence } from "framer-motion";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_minimal-traffic/artifacts/fxot6k74_Clearwayy%20logo.jpeg";
const VIDEO_URL = "https://customer-assets.emergentagent.com/job_minimal-traffic/artifacts/4f1dggu3_Clearwayy%20video.mp4";
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.15, duration: 0.6, ease: "easeOut" } }),
};

// ─── Sticky Nav ───
const NAV_LINKS = [
  { href: "#home", label: "Home" },
  { href: "#benefits", label: "Benefits" },
  { href: "#stories", label: "Stories" },
  { href: "#order", label: "Order Now" },
  { href: "#contact", label: "Contact Us" },
];

const StickyNav = () => {
  const [open, setOpen] = useState(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-xl border-b border-black/5" data-testid="sticky-nav">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2">
          <img src={LOGO_URL} alt="Clearwayy" className="h-9 w-auto" data-testid="nav-logo" />
        </a>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-8 text-sm font-medium text-black/60">
          {NAV_LINKS.map((l) => (
            <a key={l.href} href={l.href} className="hover:text-black transition-colors">{l.label}</a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <a
            href="?id=DEMO_PREVIEW"
            data-testid="demo-btn"
            className="hidden sm:inline-flex border border-black/10 text-black text-sm font-bold px-4 py-2 rounded-full hover:bg-black/5 transition-colors"
          >
            Test the Demo
          </a>
          <a
            href="#order"
            data-testid="nav-cta"
            className="hidden sm:inline-flex bg-[#FF6B00] text-white text-sm font-bold px-5 py-2.5 rounded-full hover:bg-[#e56000] transition-colors"
          >
            Get Your Kit
          </a>

          {/* Hamburger — visible below md */}
          <button
            onClick={() => setOpen(!open)}
            data-testid="hamburger-btn"
            className="md:hidden w-10 h-10 rounded-full flex items-center justify-center hover:bg-black/5 transition-colors"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5 text-black" /> : <Menu className="w-5 h-5 text-black" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="md:hidden bg-white/95 backdrop-blur-xl border-t border-black/5 px-6 pb-5 pt-2"
          data-testid="mobile-menu"
        >
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="block py-3 text-base font-medium text-black/70 hover:text-black border-b border-black/5 last:border-0 transition-colors"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#order"
            onClick={() => setOpen(false)}
            className="mt-4 block text-center bg-[#FF6B00] text-white font-bold py-3 rounded-full hover:bg-[#e56000] transition-colors sm:hidden"
          >
            Get Your Kit
          </a>
          <a
            href="?id=DEMO_PREVIEW"
            onClick={() => setOpen(false)}
            className="mt-3 block text-center border border-black/10 text-black font-bold py-3 rounded-full hover:bg-black/5 transition-colors sm:hidden"
          >
            Test the Demo
          </a>
        </motion.div>
      )}
    </nav>
  );
};

// ─── Hero ───
const headlines = [
  "Clear the Path.\nSave the Day.",
  "Your Family's Safety,\nJust a Scan Away.",
];

const HeroSection = () => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [headlineIdx, setHeadlineIdx] = useState(0);

  useEffect(() => {
    const iv = setInterval(() => setHeadlineIdx((i) => (i + 1) % headlines.length), 4000);
    return () => clearInterval(iv);
  }, []);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) { videoRef.current.pause(); }
      else { videoRef.current.play(); }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <section id="home" className="pt-28 pb-20 md:pt-36 md:pb-28 px-6" data-testid="hero-section">
      <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 md:gap-16 items-center">
        {/* Left */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}>
          <div className="h-[140px] sm:h-[160px] lg:h-[180px] relative overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.h1
                key={headlineIdx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="text-4xl sm:text-5xl lg:text-6xl font-black text-black leading-[1.08] tracking-tight whitespace-pre-line absolute inset-0"
              >
                {headlines[headlineIdx]}
              </motion.h1>
            </AnimatePresence>
          </div>
          <motion.p variants={fadeUp} custom={1} className="mt-4 text-base sm:text-lg text-black/50 leading-relaxed max-w-lg">
            Whether it's a parking hurdle or an unexpected incident, Clearwayy connects help to the right people instantly and privately.
          </motion.p>
          <motion.div variants={fadeUp} custom={2} className="mt-8 flex flex-wrap gap-4">
            <a
              href="#order"
              data-testid="hero-cta"
              className="bg-[#FF6B00] text-white font-bold px-8 py-4 rounded-full text-base hover:bg-[#e56000] transition-colors inline-flex items-center gap-2"
            >
              Get Your Kit <ChevronRight className="w-4 h-4" />
            </a>
            <a
              href="#benefits"
              className="border-2 border-black/10 text-black font-bold px-8 py-4 rounded-full text-base hover:border-black/30 transition-colors"
            >
              Learn More
            </a>
          </motion.div>
        </motion.div>

        {/* Right — Video */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="relative"
        >
          <div
            className="relative rounded-2xl overflow-hidden shadow-[0_0_60px_rgba(255,107,0,0.12)] aspect-video cursor-pointer group"
            onClick={togglePlay}
            data-testid="hero-video"
          >
            <video
              ref={videoRef}
              src={VIDEO_URL}
              className="w-full h-full object-cover"
              playsInline
              loop
              preload="metadata"
              poster=""
            />
            {!isPlaying && (
              <div className="absolute inset-0 bg-black/20 flex items-center justify-center group-hover:bg-black/30 transition-colors">
                <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                  <Play className="w-7 h-7 text-[#FF6B00] ml-1" />
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ─── Benefits Bento ───
const benefits = [
  { icon: Shield, title: "Privacy First", desc: "Your data stays yours. No numbers shared.", color: "#000" },
  { icon: Zap, title: "Instant Alerts", desc: "Real-time WhatsApp integration.", color: "#FF6B00" },
  { icon: Siren, title: "Emergency Ready", desc: "One-tap 108 / Fire / Police integration.", color: "#DC2626" },
  { icon: Smartphone, title: "Zero App Hassle", desc: "No downloads. No registrations for bystanders. Works directly in the mobile browser.", color: "#000" },
  { icon: Heart, title: "Emergency SOS", desc: "Allows the bystanders to send alerts to your designated emergency contacts in case of a major incident.", color: "#DC2626" },
  { icon: BellRing, title: "Battery / Theft Alerts", desc: "Neighbors can quietly alert you if your lights are on or they notice suspicious activity.", color: "#FF6B00" },
];

const BenefitsSection = () => (
  <section id="benefits" className="py-20 md:py-28 px-6 bg-[#FAFAFA]" data-testid="benefits-section">
    <div className="max-w-7xl mx-auto">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
        <motion.p variants={fadeUp} custom={0} className="text-[#FF6B00] font-bold text-sm uppercase tracking-widest mb-3">Why Clearwayy</motion.p>
        <motion.h2 variants={fadeUp} custom={1} className="text-3xl sm:text-4xl font-black text-black tracking-tight">Built for real life.</motion.h2>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-6">
        {benefits.map((b, i) => (
          <motion.div
            key={b.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.12, duration: 0.5, ease: "easeOut" }}
            whileHover={{ y: -8, boxShadow: "0 20px 40px -15px rgba(0,0,0,0.12)" }}
            className="rounded-3xl p-8 transition-all duration-300 cursor-default group"
            style={{
              background: "rgba(255, 255, 255, 0.7)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              border: "1px solid rgba(0,0,0,0.05)",
              boxShadow: "0 10px 30px -10px rgba(0,0,0,0.08)",
            }}
            data-testid={`benefit-card-${i}`}
          >
            <div className="border-t-2 border-transparent group-hover:border-[#FF6B00] transition-colors duration-300 -mt-8 -mx-8 mb-6 rounded-t-3xl" />
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5" style={{ background: `${b.color}10` }}>
              <b.icon className="w-6 h-6" style={{ color: b.color }} />
            </div>
            <h3 className="text-xl font-bold text-black mb-2">{b.title}</h3>
            <p className="text-black/50 text-sm leading-relaxed">{b.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// ─── Dual How It Works ───
const parkingSteps = [
  { icon: QrCode, label: "Scan", desc: "Scan the QR code on the vehicle's Clearwayy sticker." },
  { icon: MessageCircle, label: "Notify Owner", desc: "Owner gets an instant WhatsApp alert on their phone." },
  { icon: Car, label: "Move Vehicle", desc: "Owner moves the vehicle. Problem solved, no confrontation." },
];

const emergencySteps = [
  { icon: QrCode, label: "Scan", desc: "Scan the sticker on the vehicle involved in the incident." },
  { icon: Siren, label: "Select Emergency", desc: "Choose the type: medical, police, fire, or family alert." },
  { icon: Heart, label: "Help Arrives", desc: "Family, owner, and medical services are notified simultaneously." },
];

const HowItWorks = () => {
  const [tab, setTab] = useState("parking");
  const activeSteps = tab === "parking" ? parkingSteps : emergencySteps;

  return (
    <section className="py-20 md:py-28 px-6" data-testid="how-it-works">
      <div className="max-w-7xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
          <motion.p variants={fadeUp} custom={0} className="text-[#FF6B00] font-bold text-sm uppercase tracking-widest mb-3">How It Works</motion.p>
          <motion.h2 variants={fadeUp} custom={1} className="text-3xl sm:text-4xl font-black text-black tracking-tight">Three steps. That's it.</motion.h2>
        </motion.div>

        {/* Tab toggle */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-[#FAFAFA] rounded-full p-1 border border-black/5">
            <button
              onClick={() => setTab("parking")}
              data-testid="tab-parking"
              className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all ${
                tab === "parking" ? "bg-[#FF6B00] text-white shadow-sm" : "text-black/50 hover:text-black"
              }`}
            >
              Parking Alert
            </button>
            <button
              onClick={() => setTab("emergency")}
              data-testid="tab-emergency"
              className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all ${
                tab === "emergency" ? "bg-[#DC2626] text-white shadow-sm" : "text-black/50 hover:text-black"
              }`}
            >
              Emergency / Incident
            </button>
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.35 }}
            className="grid md:grid-cols-3 gap-8 md:gap-12"
          >
            {activeSteps.map((s, i) => {
              const accentColor = tab === "parking" ? "#FF6B00" : "#DC2626";
              return (
                <div key={s.label} className="text-center">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
                    style={{ background: `${accentColor}10` }}
                  >
                    <s.icon className="w-7 h-7" style={{ color: accentColor }} />
                  </div>
                  <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: accentColor }}>Step {i + 1}</p>
                  <h3 className="text-xl font-bold text-black mb-2">{s.label}</h3>
                  <p className="text-black/50 text-sm leading-relaxed max-w-xs mx-auto">{s.desc}</p>
                </div>
              );
            })}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

// ─── Wall of Love ───
const reviews = [
  { name: "Priya S.", city: "Hyderabad", stars: 5, text: "Someone was blocking my driveway. Scanned, notified, and the owner moved their car in 5 minutes. Lifesaver!" },
  { name: "Rahul M.", city: "Bangalore", stars: 5, text: "No more awkward door-knocking. The WhatsApp alert is instant and private. Brilliant concept." },
  { name: "Ananya K.", city: "Mumbai", stars: 5, text: "Used the emergency feature when a car was blocking an ambulance. Police responded immediately. Thank you Clearwayy!" },
  { name: "Vikram J.", city: "Delhi", stars: 4, text: "Simple, effective, and respects privacy. Every apartment complex needs this." },
];

const WallOfLove = () => (
  <section id="stories" className="py-20 md:py-28 px-6 bg-[#FAFAFA]" data-testid="wall-of-love">
    <div className="max-w-7xl mx-auto">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
        <motion.p variants={fadeUp} custom={0} className="text-[#FF6B00] font-bold text-sm uppercase tracking-widest mb-3">Wall of Love</motion.p>
        <motion.h2 variants={fadeUp} custom={1} className="text-3xl sm:text-4xl font-black text-black tracking-tight">What people are saying.</motion.h2>
      </motion.div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {reviews.map((r, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1, duration: 0.5, ease: "easeOut" }}
            whileHover={{ y: -8, boxShadow: "0 20px 40px -15px rgba(0,0,0,0.12)" }}
            className="rounded-2xl p-6 transition-all duration-300 cursor-default group"
            style={{
              background: "rgba(255, 255, 255, 0.7)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              border: "1px solid rgba(0,0,0,0.05)",
              boxShadow: "0 10px 30px -10px rgba(0,0,0,0.08)",
            }}
            data-testid={`review-card-${i}`}
          >
            <div className="border-t-2 border-transparent group-hover:border-[#FF6B00] transition-colors duration-300 -mt-6 -mx-6 mb-4 rounded-t-2xl" />
            <div className="flex gap-0.5 mb-4">
              {Array.from({ length: r.stars }).map((_, j) => (
                <Star key={j} className="w-4 h-4 fill-[#FF6B00] text-[#FF6B00]" />
              ))}
            </div>
            <p className="text-black/70 text-base font-light leading-relaxed mb-5 italic">"{r.text}"</p>
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-black/5 flex items-center justify-center text-xs font-black text-black/50">
                {r.name[0]}
              </div>
              <div>
                <p className="text-xs font-black text-black uppercase tracking-wider">{r.name}</p>
                <p className="text-[10px] text-black/40 font-medium">Verified User, {r.city}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// ─── Audio Feedback ───
const AudioFeedback = () => {
  const [recording, setRecording] = useState(false);
  const [audioURL, setAudioURL] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [suggestion, setSuggestion] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRef.current = new MediaRecorder(stream);
      chunksRef.current = [];
      mediaRef.current.ondataavailable = (e) => chunksRef.current.push(e.data);
      mediaRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        setAudioURL(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };
      mediaRef.current.start();
      setRecording(true);
    } catch {
      /* permission denied */
    }
  };

  const stopRecording = () => {
    if (mediaRef.current && recording) {
      mediaRef.current.stop();
      setRecording(false);
    }
  };

  const handleSubmitFeedback = async () => {
    setSubmitting(true);
    try {
      if (audioBlob) {
        const form = new FormData();
        form.append("audio", audioBlob, "recording.webm");
        form.append("suggestion", suggestion);
        await fetch(`${API_URL}/api/feedback/audio`, { method: "POST", body: form });
      } else {
        const form = new FormData();
        form.append("suggestion", suggestion);
        await fetch(`${API_URL}/api/feedback/text`, { method: "POST", body: form });
      }
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 3000);
      setSuggestion("");
      setAudioURL(null);
      setAudioBlob(null);
    } catch {
      /* silent fail */
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-20 md:py-28 px-6" data-testid="feedback-section">
      <div className="max-w-3xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
          <motion.p variants={fadeUp} custom={0} className="text-[#FF6B00] font-bold text-sm uppercase tracking-widest mb-3">Community</motion.p>
          <motion.h2 variants={fadeUp} custom={1} className="text-3xl sm:text-4xl font-black text-black tracking-tight">Your voice matters.</motion.h2>
        </motion.div>

        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1}
          className="bg-white rounded-3xl p-8 border border-black/5 shadow-sm"
        >
          {/* Audio recorder */}
          <div className="mb-6">
            <p className="text-sm font-bold text-black mb-3">Record your Clearwayy story</p>
            <div className="flex items-center gap-4">
              <button
                onClick={recording ? stopRecording : startRecording}
                data-testid="record-btn"
                className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
                  recording ? "bg-red-500 animate-pulse" : "bg-black"
                }`}
              >
                {recording ? <MicOff className="w-6 h-6 text-white" /> : <Mic className="w-6 h-6 text-white" />}
              </button>
              <div className="flex-1">
                {recording && <p className="text-red-500 text-sm font-medium">Recording...</p>}
                {audioURL && !recording && (
                  <audio src={audioURL} controls className="w-full h-10" data-testid="audio-playback" />
                )}
                {!recording && !audioURL && <p className="text-black/40 text-sm">Tap the mic to share your experience</p>}
              </div>
            </div>
          </div>

          <div className="border-t border-black/5 pt-6">
            <p className="text-sm font-bold text-black mb-3">Suggest a feature</p>
            <textarea
              value={suggestion}
              onChange={(e) => setSuggestion(e.target.value)}
              placeholder="What would make Clearwayy even better?"
              data-testid="feedback-textarea"
              className="w-full h-24 rounded-xl border border-black/10 p-4 text-sm text-black placeholder:text-black/30 resize-none focus:outline-none focus:border-[#FF6B00]/50 focus:ring-2 focus:ring-[#FF6B00]/10 transition-all"
            />
          </div>

          <button
            onClick={handleSubmitFeedback}
            disabled={(!suggestion && !audioBlob) || submitting}
            data-testid="submit-feedback-btn"
            className="mt-4 bg-[#FF6B00] text-white font-bold px-6 py-3 rounded-full text-sm hover:bg-[#e56000] transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {submitted ? (
              <>
                <CheckCircle className="w-4 h-4" /> Submitted!
              </>
            ) : submitting ? (
              "Sending..."
            ) : (
              <>
                <Send className="w-4 h-4" /> Submit Feedback
              </>
            )}
          </button>
        </motion.div>
      </div>
    </section>
  );
};

// ─── Order / CTA ───
const OrderSection = () => (
  <section id="order" className="py-20 md:py-28 px-6 bg-black" data-testid="order-section">
    <div className="max-w-3xl mx-auto text-center">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}>
        <motion.p variants={fadeUp} custom={0} className="text-[#FF6B00] font-bold text-sm uppercase tracking-widest mb-4">Get Started</motion.p>
        <motion.h2 variants={fadeUp} custom={1} className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-6">
          Get your Clearwayy Kit.
        </motion.h2>
        <motion.p variants={fadeUp} custom={2} className="text-white/50 text-base sm:text-lg mb-10 max-w-lg mx-auto leading-relaxed">
          Premium QR stickers for your vehicle. One scan is all it takes to protect your peace of mind.
        </motion.p>
        <motion.a
          variants={fadeUp}
          custom={3}
          href="https://wa.me/918106149363?text=Hi%20Clearwayy%2C%20I%20want%20to%20order%20a%20kit!"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="order-cta"
          className="inline-flex items-center gap-2 bg-[#FF6B00] text-white font-bold px-10 py-4 rounded-full text-lg hover:bg-[#e56000] transition-colors"
        >
          Order Now <ChevronRight className="w-5 h-5" />
        </motion.a>
      </motion.div>
    </div>
  </section>
);

// ─── Footer ───
const Footer = () => (
  <footer id="contact" className="py-12 px-6 border-t border-black/5" data-testid="landing-footer">
    <div className="max-w-7xl mx-auto flex flex-col items-center gap-6">
      <img src={LOGO_URL} alt="Clearwayy" className="h-12 w-auto" data-testid="footer-logo" />
      <div className="flex items-center gap-2 text-sm text-black/50">
        <span>Contact us:</span>
        <a href="mailto:support@clearwayy.in" className="text-[#FF6B00] font-medium hover:underline">support@clearwayy.in</a>
      </div>
      <p className="text-black/30 text-sm">&copy; {new Date().getFullYear()} Clearwayy. All rights reserved.</p>
    </div>
  </footer>
);

// ─── Main Landing Page ───
const LandingPage = () => (
  <div className="bg-white min-h-screen" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }} data-testid="landing-page">
    <StickyNav />
    <HeroSection />
    <BenefitsSection />
    <HowItWorks />
    <WallOfLove />
    <AudioFeedback />
    <OrderSection />
    <Footer />
  </div>
);

export default LandingPage;
